import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  GlobalToastItemData,
  GlobalToastType,
  GlobalToastViewport,
} from "../components/global-toast";

type ToastPayload = {
  title: string;
  description?: string;
  type?: GlobalToastType;
  duration?: number;
};

type ToastContextValue = {
  dismissToast: (id: string) => void;
  showToast: (payload: ToastPayload) => string;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_TOAST_DURATION = 4000;

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<GlobalToastItemData[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((previous) => previous.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    ({ title, description, type = "success", duration = DEFAULT_TOAST_DURATION }: ToastPayload) => {
      const nextId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

      setToasts((previous) => [
        ...previous,
        {
          id: nextId,
          title,
          description,
          type,
          duration,
        },
      ]);

      return nextId;
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      dismissToast,
      showToast,
    }),
    [dismissToast, showToast],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <GlobalToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }

  return context;
};
